import type { CSSProperties } from 'react';
import { ChartTable } from '../ChartTable/ChartTable.tsx';
import { Skeleton } from '../Skeleton/Skeleton.tsx';
import type { ChartPoint } from './chart-point.ts';
import { ticksOf } from './chart-scale.ts';
import styles from './ColumnChart.module.css';

export type { ChartPoint } from './chart-point.ts';

export type ColumnAxis = 'ends' | 'each';

type ChartBasics = {
  label: string;
  points: readonly ChartPoint[];
  loading?: boolean;
  highlight?: string | undefined;
};

export type ColumnChartProps = ChartBasics &
  ({ axis?: ColumnAxis } | { axis: 'grid'; formatTick: (value: number) => string });

const shareOf = (value: number, highest: number): CSSProperties =>
  ({ '--column-share': highest > 0 ? String(Math.max(value, 0) / highest) : '0' }) as CSSProperties;

const Columns = ({ points, highest, highlight }: ChartBasics & { highest: number }) =>
  points.map((point) => (
    <span
      key={point.key}
      className={styles['column']}
      data-highlight={point.key === highlight}
      style={shareOf(point.value, highest)}
      title={`${point.label} · ${point.display}`}
    />
  ));

const EachLabel = ({ points }: { points: readonly ChartPoint[] }) =>
  points.map((point) => <span key={point.key}>{point.label}</span>);

const MAX_GRID_LABELS = 8;

const SpacedLabels = ({ points }: { points: readonly ChartPoint[] }) => {
  const every = Math.ceil(points.length / MAX_GRID_LABELS);
  return points.map((point, index) => {
    const labelled = index % every === 0;
    return (
      <span key={point.key} data-minor={labelled && (index / every) % 2 === 1 ? true : undefined}>
        {labelled ? point.label : ''}
      </span>
    );
  });
};

const GridChart = ({ formatTick, ...chart }: ChartBasics & { formatTick: (value: number) => string }) => {
  const ticks = ticksOf(Math.max(0, ...chart.points.map((point) => point.value)));
  const top = ticks.at(-1) ?? 0;
  return (
    <figure className={[styles['chart'], styles['grid']].join(' ')}>
      <figcaption className="visually-hidden">{chart.label}</figcaption>
      <div className={styles['ticks']} aria-hidden="true">
        {ticks.map((tick) => (
          <span key={tick} style={shareOf(tick, top)}>
            {formatTick(tick)}
          </span>
        ))}
      </div>
      <div className={styles['plot']} aria-hidden="true">
        {ticks.map((tick) => (
          <span key={tick} className={styles['gridline']} style={shareOf(tick, top)} />
        ))}
        <Columns {...chart} highest={top} />
      </div>
      <div className={styles['eachAxis']} aria-hidden="true">
        <SpacedLabels points={chart.points} />
      </div>
      <ChartTable label={chart.label} points={chart.points} />
    </figure>
  );
};

export const ColumnChart = (props: ColumnChartProps) => {
  if (props.loading === true) return <Skeleton height="10rem" />;
  if (props.axis === 'grid') return <GridChart {...props} />;
  const { label, points, axis = 'ends' } = props;
  const highest = Math.max(0, ...points.map((point) => point.value));
  const peak = points.find((point) => point.value === highest && highest > 0);
  const first = points.at(0);
  const last = points.at(-1);
  return (
    <figure className={styles['chart']}>
      <figcaption className="visually-hidden">{label}</figcaption>
      <div className={styles['scale']} aria-hidden="true">
        {peak?.display ?? ''}
      </div>
      <div className={styles['plot']} aria-hidden="true">
        <Columns {...props} highest={highest} />
      </div>
      {axis === 'each' ? (
        <div className={styles['eachAxis']} aria-hidden="true">
          <EachLabel points={points} />
        </div>
      ) : (
        <div className={styles['axis']} aria-hidden="true">
          <span>{first?.label ?? ''}</span>
          <span>{last === first ? '' : (last?.label ?? '')}</span>
        </div>
      )}
      <ChartTable label={label} points={points} />
    </figure>
  );
};
